import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { validateGeometry, outline, center } from '@theme/cm-engraving-geometry';

const NS = 'http://www.w3.org/2000/svg';
const xml = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c]);
class CorkMartPersonalisation extends Component {
  #templateUrl;
  #controller;
  #previewUrl;
  #imageRevision = 0;
  #profileRevision = 0;
  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }
  updatedCallback() {
    super.updatedCallback();
    this.#connect();
  }
  #connect() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.fileInput = this.querySelector('input[type="file"]');
    if (this.dataset.cmFace !== 'back') this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.onProductSelect, options);
    this.instructions = this.querySelector('[data-engraving-instructions]');
    this.dropzone = this.querySelector('[data-upload-zone]');
    this.addEventListener('change', this.onChange, options);
    this.addEventListener('input', this.onInput, options);
    this.addEventListener('click', this.onClick, options);
    this.addEventListener('invalid', this.onInvalid, { ...options, capture: true });
    this.closest('form')?.addEventListener('formdata', this.cleanProperties, options);
    this.dropzone?.addEventListener('dragover', this.onDragOver, options);
    this.dropzone?.addEventListener('dragleave', this.onDragLeave, options);
    this.dropzone?.addEventListener('drop', this.onDrop, options);
    this.setInstructionsOptional(Boolean(this.querySelector('cm-laser-creator [data-artwork-mode][value="create"]:checked')));
    this.renderFile();
    const geometry = this.querySelector('[data-profile-full-diagram]')?.content.querySelector('[data-cm-engraving]');
    if (geometry) { try { validateGeometry(geometry.dataset); this.prepareTemplate(geometry); } catch (error) { this.rejectProfile(error); } }
  }
  disconnectedCallback() { super.disconnectedCallback();this.#controller?.abort();this.releasePreview();this.releaseTemplate();this.#profileRevision++; }
  releaseTemplate() { if (this.#templateUrl) URL.revokeObjectURL(this.#templateUrl); this.#templateUrl = undefined; }
  prepareTemplate(diagram) {
    this.releaseTemplate();
    const link = this.querySelector('[data-download-template]');
    if (!link) return;
    link.hidden = true; link.removeAttribute('href');
    this.querySelector('[data-template-download]').hidden = diagram?.dataset.verified !== 'true';
    if (diagram?.dataset.verified !== 'true') return;
    const labelsData = link.dataset;
    const d = diagram.dataset; const pw = Number(d.productWidth); const ph = Number(d.productHeight); const aw = Number(d.areaWidth); const ah = Number(d.areaHeight); const margin = Number(d.margin);
    const pageWidth = Math.max(pw + 20, 140); const left = (pageWidth - pw) / 2; const top = 10;
    let cx = left + pw / 2; let cy = top + ph / 2;
    if (d.position === 'top') cy = top + margin + ah / 2;
    if (d.position === 'bottom') cy = top + ph - margin - ah / 2;
    if (d.position === 'left') cx = left + margin + aw / 2;
    if (d.position === 'right') cx = left + pw - margin - aw / 2;
    let outer = d.shape === 'circle' ? `<circle cx="${left + pw / 2}" cy="${top + ph / 2}" r="${pw / 2}"/>` : `<rect x="${left}" y="${top}" width="${pw}" height="${ph}"/>`;
    let inner = d.shape === 'circle' ? `<ellipse cx="${cx}" cy="${cy}" rx="${aw / 2}" ry="${ah / 2}"/>` : `<rect x="${cx - aw / 2}" y="${cy - ah / 2}" width="${aw}" height="${ah}"/>`;
    if (!['circle','rectangle'].includes(d.shape) || d.areaShape !== d.shape || Number(d.offsetX) || Number(d.offsetY) || Number(d.rotation)) {
      const c = center(d); cx = left + c[0]; cy = top + c[1];
      outer = outline(d, false, left, top); inner = `<g transform="rotate(${Number(d.rotation||0)} ${cx} ${cy})">${outline(d, true, cx-aw/2, cy-ah/2)}</g>`;
    }
    const symbol = d.shape === 'circle' ? 'Ø' : '';
    const labels = [labelsData.templateTitle, `${labelsData.templateProduct}: ${symbol}${pw}${d.shape === 'rectangle' ? ' × ' + ph : ''} mm`, `${labelsData.templateArea}: ${symbol}${aw}${d.shape === 'rectangle' ? ' × ' + ah : ''} mm`, `${labelsData.templateMargin}: ${margin} mm`, labelsData.templateLegend];
    const svg = `<svg xmlns="${NS}" width="${pageWidth}mm" height="${ph + 52}mm" viewBox="0 0 ${pageWidth} ${ph + 52}"><title>${xml(labelsData.templateTitle)}</title><desc>${xml(diagram.querySelector('desc').textContent)}</desc><g fill="none" stroke="black" stroke-width="0.3">${outer}<g stroke-dasharray="2 1">${inner}</g><path d="M ${left} ${cy} H ${cx - aw / 2}"/></g><g fill="black" font-family="sans-serif" font-size="3">${labels.map((label,i) => `<text x="5" y="${ph + 19 + i * 6}">${xml(label)}</text>`).join('')}</g></svg>`;
    this.#templateUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    link.href = this.#templateUrl; link.download = `corkmart-template-${labelsData.productHandle}.svg`; link.hidden = false;
  }
  rejectProfile(error) {
    this.releaseTemplate();
    if (this.dataset.cmFace === 'back') { this.parentElement.closest('cm-personalisation')?.rejectProfile(error); return; }
    this.querySelector('[data-face-panel="back"]')?.remove();
    this.querySelector('[data-laser-extras]')?.replaceChildren();
    this.querySelector('cm-laser-faces')?.sync();
    this.dataset.profileStatus = 'invalid';
    this.querySelector('cm-laser-creator')?.remove();
    this.querySelector('cm-laser-panel[data-ready]')?.setCompletion(false);
    for (const field of this.querySelectorAll('[data-profile-property]')) if (field.closest('cm-personalisation') === this) field.remove();
    this.querySelector('[data-file-route]').hidden = false;
    this.querySelector('[data-template-download]').hidden = true;
    this.fileInput.required = true; this.fileInput.setCustomValidity('');
    this.setInstructionsOptional(false); this.renderFile();
    for (const figure of document.querySelectorAll('[data-profile-display]')) figure.hidden = true;
    console.warn('Cork Laser Studio: file-only profile', error?.message || 'Incomplete profile');
  }
  onProductSelect = async (event) => {
    if (event.target.closest('product-card,quick-add-dialog')) return;
    const revision = ++this.#profileRevision;
    const current = this.querySelector('cm-laser-creator');
    try {
      const result = await event.promise;
      if (!this.isConnected || revision !== this.#profileRevision) return;
      const next = result?.detail?.html?.querySelector('cm-personalisation[data-cm-face="' + (this.dataset.cmFace || 'front') + '"]');
      if (!next) throw new Error('Missing profile response');
      const extras = this.dataset.cmFace === 'back' ? null : this.querySelector('[data-laser-extras]');
      const freshExtras = next.querySelector('[data-laser-extras]');
      if (extras && freshExtras) {
        const selected = [...extras.querySelectorAll(':checked')].map(input => input.dataset.code);
        const replacement = freshExtras.cloneNode(true);
        for (const input of replacement.querySelectorAll('[data-laser-extra]')) input.checked = selected.includes(input.dataset.code);
        extras.replaceWith(replacement);
      }
      const record = next.dataset.profileRecord;
      if (record !== this.dataset.profileRecord) {
        const oldBack = this.querySelector('[data-face-panel="back"]');
        const newBack = next.querySelector('[data-face-panel="back"]');
        if (oldBack && newBack) oldBack.replaceWith(newBack.cloneNode(true));
        else if (oldBack) oldBack.remove();
        else if (newBack) this.querySelector('cm-laser-faces').append(newBack.cloneNode(true));
        if (newBack) await import(this.dataset.faceModule);
        this.querySelector('cm-laser-panel')?.setCompletion(false);
      }
      if (record === this.dataset.profileRecord) { current?.syncGallery(); this.querySelector('cm-laser-faces')?.sync(); return; }
      const template = next.querySelector('[data-profile-full-diagram]');
      const figure = template?.content.querySelector('[data-cm-engraving]');
      this.dataset.profileRecord = record;
      this.dataset.profileStatus = next.dataset.profileStatus;
      this.dataset.areaWidth = next.dataset.areaWidth; this.dataset.areaHeight = next.dataset.areaHeight;
      for (const field of this.querySelectorAll('[data-profile-property]')) if (field.closest('cm-personalisation') === this) field.remove();
      for (const field of next.querySelectorAll('[data-profile-property]')) if (field.closest('cm-personalisation') === next) this.append(field.cloneNode(true));
      if (next.dataset.profileStatus !== 'valid' || !figure) { this.rejectProfile(); return; }
      validateGeometry(figure.dataset);
      const previousTemplate = this.querySelector('[data-profile-full-diagram]');
      previousTemplate.replaceWith(template.cloneNode(true));
      this.prepareTemplate(figure);
      if (this.dataset.cmFace !== 'back') for (const target of document.querySelectorAll('[data-profile-public]')) {
        const verified = figure.dataset.verified === 'true';
        target.replaceChildren(...(verified ? [figure.cloneNode(true)] : []));
        const heading = target.closest('.cm-pdp-row')?.querySelector('[data-profile-heading]');
        if (heading) heading.textContent = verified ? heading.dataset.areaLabel : heading.dataset.detailsLabel;
      }
      const fresh = next.querySelector('cm-laser-creator');
      if (fresh && current?.isConnected) current.applyProfile(fresh);
      else if (fresh) {
        await import(this.dataset.creatorModule);
        if (!this.isConnected || revision !== this.#profileRevision) return;
        this.querySelector('[data-creator-slot]').after(fresh.cloneNode(true));
      }
      this.renderFile(); this.querySelector('cm-laser-faces')?.sync();
    } catch(error) { if (revision === this.#profileRevision && error.name !== 'AbortError') this.rejectProfile(error); }
  };
  setInstructionsOptional(optional) {
    this.instructions = this.querySelector('[data-engraving-instructions]');
    if (!this.instructions) return;
    this.instructions.required = !optional;
    this.instructions.setAttribute('aria-required', String(!optional));
    const label = this.querySelector('[data-instructions-label]');
    if (label) label.textContent = optional ? label.dataset.optionalLabel : label.dataset.requiredLabel;
    this.validateInstructions();
    if (this.instructions.validity.valid) this.setFieldError(this.instructions, '');
  }
  cleanProperties = event => {
    const prefix = this.dataset.cmFace === 'back' ? 'Back ' : '';
    for (const key of ['Engraving instructions','Artwork link']) {
      const property = 'properties[' + (prefix ? prefix + key[0].toLowerCase() + key.slice(1) : key) + ']';
      const value = event.formData.get(property); if (typeof value === 'string' && !value.trim()) event.formData.delete(property);
    }
  };
  validateInstructions() { this.instructions?.setCustomValidity(!this.instructions.required || this.instructions.value.trim() ? '' : this.dataset.instructionsRequired); }
  onInput = (event) => {
    if (event.target.closest('cm-personalisation') !== this) return;
    if (event.target === this.instructions) this.validateInstructions();
    if (event.target.matches('textarea,input[type="url"]') && event.target.getAttribute('aria-invalid') === 'true') {
      this.setFieldError(event.target, event.target.validity.valid ? '' : event.target === this.instructions ? this.dataset.instructionsRequired : event.target.matches('[data-laser-text]') ? event.target.validationMessage : this.dataset.invalidLink);
    }
  };
  setFieldError(field, message) {
    field.setAttribute('aria-invalid', message ? 'true' : 'false');
    const output = field === this.instructions ? this.querySelector('[data-instructions-error]') : field === this.fileInput ? this.querySelector('[data-upload-status]') : field.matches('[data-laser-text]') ? this.querySelector('[data-creator-status]') : this.querySelector('[data-link-error]');
    if (output) output.textContent = message;
  }
  onInvalid = (event) => {
    const field = event.target;
    if (field.closest('cm-personalisation') !== this) return;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    event.preventDefault();
    this.setAttribute('data-validation-attempted', '');
    const message = field === this.instructions ? this.dataset.instructionsRequired : field === this.fileInput || field.matches('[data-laser-text]') ? field.validationMessage : this.dataset.invalidLink;
    this.setFieldError(field, message);
    const panel = field.closest('details');
    if (panel) panel.open = true;
    const first = this.querySelector('[aria-invalid="true"]');
    const personaliser = first?.closest('cm-laser-panel[data-ready]');
    if (personaliser) personaliser.revealError(first);
    else {
      first?.focus({ preventScroll: true });
      first?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  };
  onChange = (event) => { if (event.target === this.fileInput) this.renderFile(); };
  onDragOver = (event) => { if (!event.dataTransfer?.types.includes('Files')) return;event.preventDefault();event.dataTransfer.dropEffect = 'copy';this.dropzone.classList.add('is-dragging'); };
  onDragLeave = () => this.dropzone.classList.remove('is-dragging');
  onDrop = (event) => {
    event.preventDefault();this.dropzone.classList.remove('is-dragging');
    const files = event.dataTransfer?.files;if (!files?.length) return;
    if (files.length > 1) { this.showFileError(this.dataset.oneFile);return; }
    this.fileInput.files = files;this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  };
  onClick = (event) => {
    if (event.target.closest('cm-personalisation') !== this || !event.target.closest('[data-remove-file]')) return;
    this.fileInput.value = '';this.fileInput.setCustomValidity('');this.renderFile();
    this.querySelector('[data-upload-status]').textContent = this.dataset.fileRemoved;this.fileInput.focus();
  };
  releasePreview() { if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);this.#previewUrl = undefined; }
  showFileError(message) {
    this.releasePreview();this.fileInput.value = '';this.fileInput.setCustomValidity(message);
    this.querySelector('[data-upload-status]').textContent = message;
    this.fileInput.setAttribute('aria-invalid', 'true');
    this.querySelector('[data-upload-file]').hidden = false;this.querySelector('[data-file-name]').textContent = message;this.querySelector('[data-file-preview]').hidden = true;
  }
  renderFile() {
    if (!this.fileInput) return;this.releasePreview();
    const revision = ++this.#imageRevision;
    const warning = this.querySelector('[data-resolution-warning]'); if (warning) { warning.hidden = true; delete warning.dataset.pxPerMm; }
    const file = this.fileInput.files?.[0];const card = this.querySelector('[data-upload-file]');const image = this.querySelector('[data-file-preview]');const status = this.querySelector('[data-upload-status]');
    card.hidden = !file;image.hidden = true;image.removeAttribute('src');status.textContent = '';this.fileInput.setCustomValidity('');this.fileInput.setAttribute('aria-invalid', 'false');if (!file) { if (!this.querySelector('cm-laser-creator [data-artwork-mode][value="create"]:checked')) this.fileInput.setCustomValidity(this.dataset.artworkRequired); return; }
    const extension = '.' + file.name.split('.').pop().toLowerCase();const accepted = this.fileInput.accept.split(',').map((value) => value.trim().toLowerCase());
    if (!accepted.includes(extension)) { this.showFileError(this.dataset.invalidFile);return; }
    if (file.size > Number(this.dataset.maxFileSize)) { this.showFileError(this.dataset.fileTooLarge);return; }
    this.querySelector('[data-file-name]').textContent = file.name;
    if (file.type.startsWith('image/')) {
      image.onload = () => {
        if (revision !== this.#imageRevision || !this.isConnected || !['.png','.jpg','.jpeg','.webp'].includes(extension)) return;
        const geometry = this.querySelector('[data-cm-engraving]')?.dataset ?? this.dataset;
        const width = Number(geometry?.areaWidth); const height = Number(geometry?.areaHeight);
        if (!warning || !(width > 0 && height > 0)) return;
        const resolution = Math.min(image.naturalWidth / width, image.naturalHeight / height);
        warning.dataset.pxPerMm = String(resolution); warning.dataset.pixelWidth = String(image.naturalWidth); warning.dataset.pixelHeight = String(image.naturalHeight); warning.hidden = resolution >= 10;
      };
      this.#previewUrl = URL.createObjectURL(file);image.src = this.#previewUrl;image.hidden = false;image.onerror = () => { image.hidden = true;this.releasePreview(); }; }
  }
}
if (!customElements.get('cm-personalisation')) customElements.define('cm-personalisation', CorkMartPersonalisation);
